<script setup lang="ts">
// Uses default layout (with header/footer)
import { libelleLieuCourt } from "@donoor/utils";
import type { ChampsAdresseOrganisation, DerniereActivitePublique } from "@donoor/types";
import { stripHtml, truncateForMeta } from "@donoor/utils/string";
import { buildOrganizationSchema } from "~/composables/useOrganizationSchema";
import { villeOrganisation } from "#shared/utils/villeAnnuaire";
import {
  libellesCategorieAsso,
  urlLogoAsso,
} from "~/utils/associationEntete";
import { useChatPageContext, euros, phrasePaysAssociation } from "~/composables/useChatPageContext";
import { phraseDon, useAnnonceDerniereActivite, useToastEnDirect } from "~/composables/useToastEnDirect";
import FaqSectionApple from "~/components/shared/FaqSectionApple.vue";
import { aDesInfosAsso } from "~/utils/associationInfos";

const route = useRoute();
const slug = (route.params as { slug: string }).slug;
const { t } = useI18n();
const { formatDate: formatDateLocale, formatCurrency: formatCurrencyLocale } = useFormatLangue();
const config = useRuntimeConfig();
const apiUrl = config.public.apiBaseUrl;
const assetsBaseUrl = config.public.apiUrl;
const assetsUrl = `${apiUrl}/api/assets`;

// ============================================================
// SSR: Fetch organization data
// ============================================================
// Adresse structurée (lot 4, migration 307) : morceaux et point de la carte,
// facultatifs (le site part avant l'API ; point exposé seulement s'il décrit
// l'adresse actuelle).
interface PublicOrganization extends ChampsAdresseOrganisation {
  /** Dernier don récent sur les cagnottes affichées (cf. API). */
  derniere_activite?: DerniereActivitePublique | null;
  /** Flag : asso identifiée mais profil pas encore configuré (RNA manquant). */
  incomplete?: boolean;
  id: number;
  name: string;
  slug: string;
  logo: string | null;
  /** Couleur des boutons choisie dans Ma page publique → Paramètres (#rrggbb). */
  accent_color?: string | null;
  cover_image: string | null;
  // Bannière de secours (cagnotte la plus dotée) si pas de cover_image (API).
  fallback_banner?: string | null;
  description: string | null;
  website: string | null;
  full_address: string | null;
  objet_social: string | null;
  category: string | null;
  is_verified: boolean;
  rna?: string | null;
  siret?: string | null;
  /** Association hors France (migration 353) : numéro vérifié au registre du pays. */
  numero_officiel?: string | null;
  numero_officiel_type?: string | null;
  numero_officiel_verifie_at?: string | null;
  payment_slug: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  /** Pays ISO de l'asso (France à défaut) — téléphone lu dans ce pays. */
  country?: string | null;
  date_created: string | null;
  welcome_message: string | null;
  youtube_url: string | null;
  social_links: { facebook?: string; instagram?: string; twitter?: string; youtube?: string; tiktok?: string; linkedin?: string } | null;
  opening_hours: string | Record<string, unknown> | null;
  total_raised: number;
  total_donors: number;
  display: {
    show_total_raised: boolean;
    show_total_donors: boolean;
    show_campaigns_count: boolean;
    show_events_count: boolean;
    show_qr_codes: boolean;
    show_opening_hours: boolean;
  };
  campaigns: Array<{
    id: number;
    title: string;
    slug: string;
    description: string | null;
    image: string | null;
    goal_amount: number;
    currency: string;
    category: string | null;
    raised: number;
    donors_count: number;
    percent: number;
    is_verified?: boolean;
    /** La cagnotte publie ses donateurs : nom et montant du don en direct. */
    show_donors_list?: boolean;
  }>;
  events: Array<{
    id: string;
    title: string;
    slug: string;
    cover_image: string | null;
    start_date: string | null;
    location_name: string | null;
    location_city: string | null;
    category: string | null;
    status: string;
  }>;
  qr_codes: Array<{ id: string; title: string; file_id: string | null }>;
  gallery: Array<{ id: number; directus_files_id: string }>;
  posts: Array<{ id: string; title: string; content: string | null; image: string | null; date_created: string | null }>;
  posts_total: number;
  faq: Array<{ id: number; question: string; answer: string }>;
  testimonials: Array<{ id: number; author_name: string; role: string | null; content: string; rating: number | null; image: string | null }>;
  documents: Array<{ id: number; title: string; file: string }>;
  campaigns_total: number;
  events_total: number;
  membership_plans: Array<{
    id: string;
    title: string;
    slug: string;
    description: string | null;
    cover_image: string | null;
    price: string;
    duration_type: string;
    category: string | null;
  }>;
  membership_plans_total: number;
  shops: Array<{
    id: string;
    title: string;
    slug: string;
    description: string | null;
    cover_image: string | null;
  }>;
  shops_total: number;
  // Surveillance / Trust & Safety
  risk_status?:
    | "clean"
    | "documents_requested"
    | "watch"
    | "restricted"
    | "suspended"
    | "banned"
    | null;
  risk_reason?: string | null;
}

// `nf` : 404 API explicite (asso inexistante/supprimée) — distinct du null
// générique (panne API) pour ne JAMAIS envoyer un 404 sur une asso vivante
// pendant une erreur transitoire. Audit SEO 2026-08-01.
const { data: org } = await useAsyncData(
  `org-public-${slug}`,
  async (): Promise<{ o: PublicOrganization | null; nf: boolean }> => {
    try {
      const res = await $fetch<{ success: boolean; data: PublicOrganization }>(
        `${apiUrl}/api/organizations/public/slug/${slug}`
      );
      return { o: res.success ? res.data : null, nf: !res.success };
    } catch (e: unknown) {
      const status =
        (e as { statusCode?: number; status?: number })?.statusCode ??
        (e as { status?: number })?.status;
      return { o: null, nf: status === 404 };
    }
  },
  { server: true, lazy: false }
);

const _ssr = org.value?.o ?? null;

// Couleur des boutons choisie par l'association (« Soutenir », header).
useCouleurBoutonsSurLaPage(computed(() => _ssr));

// Vrai statut HTTP 404 côté SSR : Google reçoit le bon signal (avant : 200 +
// noindex = soft 404). L'humain voit la page « introuvable » + suggestions.
if (import.meta.server && org.value?.nf) {
  const event = useRequestEvent();
  if (event) setResponseStatus(event, 404);
}

// Contexte donné à l'IA du chat : elle aide en priorité sur CETTE association
// tant que le visiteur ne change pas de sujet (Karim 2026-10-12).
const { definirContexte, effacerContexte } = useChatPageContext();
watchEffect(() => {
  const o = org.value?.o;
  if (!o) return;
  const presentation = o.welcome_message || o.description;
  const activites = [
    o.campaigns_total ? `${o.campaigns_total} cagnotte(s)` : null,
    o.events_total ? `${o.events_total} événement(s)` : null,
    o.membership_plans_total ? `${o.membership_plans_total} adhésion(s)` : null,
    o.shops_total ? `${o.shops_total} boutique(s)` : null,
  ].filter(Boolean);
  const phrases = [
    `Page de l'association ${o.name}.`,
    presentation ? `Présentation : ${stripHtml(presentation).slice(0, 600)}` : null,
    o.display?.show_total_raised && o.total_raised
      ? `${euros(o.total_raised)} collectés au total${o.display?.show_total_donors && o.total_donors ? ` auprès de ${o.total_donors} donateur(s)` : ""}.`
      : null,
    activites.length ? `Propose actuellement : ${activites.join(", ")}.` : null,
    phrasePaysAssociation(o.country),
  ].filter(Boolean);
  definirContexte("association", phrases.join(" "));
});
onUnmounted(() => effacerContexte());

// ============================================================
// SEO
// ============================================================
const canonicalUrl = `https://donoor.org/associations/${slug}`;
const ogTitle = _ssr?.name || t("association.header.categorieParDefaut");
// Libellés partagés avec les pages « tout lister » (cf. associationEntete).
const categoryLabel = libellesCategorieAsso();
const orgType = _ssr?.category ? (categoryLabel[_ssr.category] || t("association.header.categorieParDefaut")) : (_ssr?.objet_social || t("association.header.categorieParDefaut"));

// Titre enrichi SEO : « {Nom} — Association {type} à {Ville} » pour matcher
// les recherches réelles (« sum asso », « association musique sèvres »).
// Pas de suffixe « | Donoor » (Karim 2026-07-16 : on cible les recherches de
// l'asso, pas de la plateforme). On APPEND du contexte au nom saisi par
// l'asso — on ne réécrit jamais son contenu.
//
// Ville (lot 4) : `villeOrganisation`, la MÊME clé que le hub ville (morceaux
// enregistrés, homonymes séparés) ; lieu affiché avec son territoire ou son
// pays hors métropole : « à Sainte-Luce, Martinique », « à Bruxelles, Belgique ».
const orgVille = villeOrganisation(_ssr);
const orgLieu = orgVille ? libelleLieuCourt(orgVille) : null;
const typeForTitle = _ssr?.category
  ? categoryLabel[_ssr.category] || t("association.header.categorieParDefaut")
  : t("association.header.categorieParDefaut");
const seoTitle = _ssr?.name
  ? (orgLieu
      ? t("association.page.titreSeoAvecVille", { nom: _ssr.name, type: typeForTitle, ville: orgLieu })
      : t("association.page.titreSeoSansVille", { nom: _ssr.name, type: typeForTitle }))
  : t("association.header.categorieParDefaut");

// Fil d'Ariane visible (SeoBreadcrumb émet aussi le JSON-LD BreadcrumbList —
// source unique, l'ancien schema manuel du useHead ci-dessous a été retiré).
// Niveau ville → hub programmatique /associations/ville/{slug} : chaque page
// asso donne un lien interne à son hub, chaque hub lie ses assos (maillage).
const breadcrumbItems = [
  { name: t("association.page.breadcrumbAssociations"), path: "/recherche/associations" },
  ...(orgVille
    ? [{ name: orgVille.nom, path: `/associations/ville/${orgVille.cle}` }]
    : []),
  { name: _ssr?.name || t("association.header.categorieParDefaut") },
];
// Meta description : la description saisie, sinon l'objet social RNA (texte
// propre à l'asso, bien meilleur pour Google qu'une phrase générique
// identique sur 50 fiches — audit SEO 2026-08-29), sinon la phrase générique.
const ogDescription = _ssr?.description
  ? truncateForMeta(stripHtml(_ssr.description), 150)
  : _ssr?.objet_social
    ? truncateForMeta(stripHtml(_ssr.objet_social), 150)
    : t("association.page.ogDescriptionDefaut", { nom: _ssr?.name || t("association.page.cetteAssociation"), type: orgType });
// OG image DYNAMIQUE (Karim 2026-08-04) : carte 1200×630 générée à la volée —
// bannière de l'asso (cover_image → bannière de secours = cagnotte la plus
// dotée → dégradé de marque) + nom + catégorie + ville + logo. Même facture que
// l'OG cagnotte, rendu de partage soigné. AVANT : simple cover_image brute ou
// logo, voire /og-image.png générique (le partage montrait une image fade).
// Fallback cover_image brute si le slug est inconnu (ex. SSR sans slug).
const ogImageUrl = slug
  ? `${useShareBaseUrl()}/api/og/organization?slug=${slug}`
  : _ssr?.cover_image
    ? `${assetsBaseUrl}/api/assets/${_ssr.cover_image}?width=1200&height=630&fit=cover&format=jpg&quality=80`
    : `${useShareBaseUrl()}/og-image.png`;

useHead({
  titleTemplate: "",
});

// Karim 26/10/2026 : « même s'il y a peu d'info elles doivent être indexées »
// — toute fiche qui existe est indexable (HelloAsso indexe aussi ses fiches
// vides). On enrichit les fiches minces (AssociationsPageFicheIdentite) au
// lieu de les cacher. Seule une association introuvable reste en noindex.
const shouldNoindex = !_ssr;
// robots cohérent avec l'indexabilité globale (staging = noindex).
const robotsContent = useRobotsContent(() => shouldNoindex);

useSeoMeta({
  title: seoTitle,
  description: ogDescription,
  robots: () => robotsContent.value,
  ogType: "website",
  ogTitle,
  ogDescription,
  ogImage: ogImageUrl,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogUrl: canonicalUrl,
  ogSiteName: "Donoor",
  ogLocale: "fr_FR",
  twitterCard: "summary_large_image",
  twitterTitle: ogTitle,
  twitterDescription: ogDescription,
  twitterImage: ogImageUrl,
});

// SEO sprint v4 : LocalBusiness/NGO schema enrichi (knowledge panel Google
// + carte Maps + horaires + apparition dans les résultats locaux).
// Mapping catégorie → sous-type Schema.org (SportsClub, ReligiousOrganization,
// EducationalOrganization, etc.) pour préciser l'entité.
const enrichedOrgSchema = _ssr
  ? buildOrganizationSchema(_ssr, {
      canonicalUrl,
      assetsBaseUrl: assetsUrl,
      description: ogDescription,
    })
  : null;

useHead({
  link: [{ rel: "canonical", key: "canonical", href: canonicalUrl }],
  script: [
    ...(enrichedOrgSchema
      ? [
          {
            type: "application/ld+json" as const,
            innerHTML: JSON.stringify(enrichedOrgSchema),
          },
        ]
      : []),
    // BreadcrumbList JSON-LD : émis par <SeoBreadcrumb> (fil d'Ariane visible
    // du template) — plus de schema manuel ici pour éviter le doublon.
  ],
});

// ============================================================
// Helpers
// ============================================================
// stripHtml importé depuis @donoor/utils/string (source unique cross-app).
// Note : avant utilisait une variante minimale `.replace(/<[^>]*>/g, "")` —
// le canonique fait MIEUX (décode aussi &nbsp; &amp; etc. + normalise espaces).

const getImageUrl = (fileId: string | null, width = 400, height = 250) => {
  if (!fileId) return null;
  return `${assetsUrl}/${fileId}?width=${width}&height=${height}&fit=cover&format=webp&quality=80`;
};

const getLogoUrl = urlLogoAsso(assetsUrl);

const formatAmount = (amount: number) =>
  formatCurrencyLocale(amount, { decimals: 0 });

const youtubeEmbedUrl = computed(() => {
  const url = _ssr?.youtube_url;
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
});

const hasSocialLinks = computed(() => {
  const s = _ssr?.social_links;
  if (!s) return false;
  return Object.values(s).some((v) => !!v);
});

// Date « jour mois(long) année » dans la langue de la page (remplace l'ancien
// `toLocaleDateString("fr-FR", …)` en dur — cf. docs/architecture/i18n.md).
const formatDate = (dateStr: string | null) =>
  dateStr ? formatDateLocale(dateStr, { format: "medium" }) : null;

// Opening hours formatter (handles both string and object from DB)
type OpeningHoursEntry = { open?: string; close?: string; closed?: boolean } | string | null | undefined;
const formatOpeningHours = (data: unknown): string => {
  if (typeof data === "string") return data;
  if (!data || typeof data !== "object") return "";
  // Handle JSON object format like { "Lundi": { "open": "09:00", "close": "18:00", "closed": false } }
  return Object.entries(data as Record<string, OpeningHoursEntry>)
    .map(([day, info]) => {
      if (info && typeof info === "object") {
        if (info.closed) return `${day} : ${t("association.page.ferme")}`;
        if (info.open && info.close) return `${day} : ${info.open.replace(":", "h")} - ${info.close.replace(":", "h")}`;
      }
      return `${day} : ${info}`;
    })
    .join("\n");
};

// Le chapitre « Informations » a-t-il au moins une tuile ? (règle unique,
// partagée avec AssociationsPageInfoColumn)
const aDesInfos = computed(() => !!_ssr && aDesInfosAsso(_ssr, hasSocialLinks.value));

// Apparition douce des chapitres au défilement (charte « apple »).
const racine = ref<HTMLElement | null>(null);
useApparitionAuDefilement(racine);

// Section navigation
const sections = computed(() => {
  if (!_ssr) return [];
  const s: { id: string; label: string; icon: string }[] = [];
  // Colonne droite (visuel mobile: 1er)
  if (_ssr.campaigns.length > 0) s.push({ id: 'campaigns', label: t('association.rightColumn.cagnottesTitre'), icon: 'i-lucide-heart' });
  if (_ssr.events.length > 0) s.push({ id: 'events', label: t('association.rightColumn.evenementsTitre'), icon: 'i-lucide-calendar' });
  if (_ssr.membership_plans?.length > 0) s.push({ id: 'memberships', label: t('association.rightColumn.adhesionsTitre'), icon: 'i-lucide-id-card' });
  if (_ssr.shops?.length > 0) s.push({ id: 'shops', label: t('association.rightColumn.boutiqueTitre'), icon: 'i-lucide-shopping-bag' });
  if (_ssr.qr_codes.length > 0) s.push({ id: 'qr-codes', label: t('association.rightColumn.qrCodesTitre'), icon: 'i-lucide-qr-code' });
  if (_ssr.posts.length > 0) s.push({ id: 'posts', label: t('association.rightColumn.actualitesTitre'), icon: 'i-lucide-newspaper' });
  // Chapitre « Informations » (carte, contact, horaires, documents) puis
  // galerie — pleine largeur depuis la refonte « apple » (Karim 2026-10-18),
  // donc plus réservés au mobile. Même règle d'affichage que le chapitre
  // (AssociationsPageInfoColumn).
  if (aDesInfos.value) s.push({ id: 'info', label: t('association.infoColumn.informations'), icon: 'i-lucide-contact' });
  if (_ssr.gallery.length > 0) s.push({ id: 'gallery', label: t('association.page.galerie'), icon: 'i-lucide-images' });
  if (_ssr.testimonials.length > 0) s.push({ id: 'testimonials', label: t('association.page.avis'), icon: 'i-lucide-message-circle' });
  if (_ssr.faq.length > 0) s.push({ id: 'faq', label: t('association.page.faq'), icon: 'i-lucide-help-circle' });
  return s;
});

const activeSection = ref('');

function scrollToSection(id: string) {
  activeSection.value = id;
  const el = document.getElementById(`section-${id}`);
  if (el) {
    const offset = 72;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}

// ============================================================
// Modals "Voir plus" avec pagination
// ============================================================
type ModalSection = 'campaigns' | 'events' | 'memberships' | 'posts';
// ModalItem peut représenter campaign | event | membership | post : champs hétérogènes.
// On garde un type indexable pour permettre l'accès dynamique aux champs dans le template.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ModalItem = Record<string, any>;
const modalOpen = ref<ModalSection | null>(null);
const modalItems = ref<ModalItem[]>([]);
const modalLoading = ref(false);
const modalLoadingMore = ref(false);
const modalHasMore = ref(false);
const modalOffset = ref(0);
const MODAL_PAGE_SIZE = 12;

async function openModal(section: ModalSection) {
  modalOpen.value = section;
  modalItems.value = [];
  modalOffset.value = 0;
  modalHasMore.value = false;
  await fetchModalPage(section, 0, true);
}

function closeModal() {
  modalOpen.value = null;
  modalItems.value = [];
}

// Modal "article complet" : ouvert au clic sur une carte actualité (colonne de
// droite ou liste "Toutes les actualités"). Affiche le contenu intégral formaté.
const selectedPost = ref<ModalItem | null>(null);
function openPost(post: ModalItem) {
  selectedPost.value = post;
}
function closePost() {
  selectedPost.value = null;
}

async function fetchModalPage(section: ModalSection, offset: number, initial = false) {
  if (initial) modalLoading.value = true;
  else modalLoadingMore.value = true;

  try {
    const endpoint = section === 'memberships' ? 'membership-plans' : section;
    const res = await $fetch<{ success: boolean; data: ModalItem[]; pagination: { hasMore: boolean } }>(
      `${apiUrl}/api/organizations/public/slug/${slug}/${endpoint}?offset=${offset}&limit=${MODAL_PAGE_SIZE}`
    );
    if (res.success) {
      if (initial) {
        modalItems.value = res.data;
      } else {
        modalItems.value = [...modalItems.value, ...res.data];
      }
      modalHasMore.value = res.pagination.hasMore;
      modalOffset.value = offset + res.data.length;
    }
  } finally {
    modalLoading.value = false;
    modalLoadingMore.value = false;
  }
}

function loadMore() {
  if (modalOpen.value) fetchModalPage(modalOpen.value, modalOffset.value);
}

const isNavSticky = ref(false);
const navRef = ref<HTMLElement | null>(null);
const navListRef = ref<HTMLElement | null>(null);
const navLinksRowRef = ref<HTMLElement | null>(null);
const navButtonRefs = ref<HTMLElement[]>([]);
const navHeight = ref(0);

// Active indicator position
const indicatorStyle = ref({ left: '0px', width: '0px' });

function updateIndicator() {
  if (!activeSection.value) return;
  const idx = sections.value.findIndex(s => s.id === activeSection.value);
  if (idx === -1) return;
  const btn = navButtonRefs.value[idx];
  if (!btn || !navListRef.value) return;
  const listRect = navListRef.value.getBoundingClientRect();
  const btnRect = btn.getBoundingClientRect();
  indicatorStyle.value = {
    left: `${btnRect.left - listRect.left}px`,
    width: `${btnRect.width}px`,
  };
  // Auto-scroll the nav row so the active button is centered
  if (navLinksRowRef.value) {
    const row = navLinksRowRef.value;
    const rowRect = row.getBoundingClientRect();
    const btnCenter = btnRect.left - rowRect.left + row.scrollLeft + btnRect.width / 2;
    const targetScroll = btnCenter - row.offsetWidth / 2;
    row.scrollTo({ left: targetScroll, behavior: 'smooth' });
  }
}

watch(activeSection, () => nextTick(updateIndicator));

// ───────────────────────────────────────────────────────────────────────
// Temps réel : les montants des cagnottes affichées ici bougent en direct.
//
// Le serveur émet déjà `campaign:donation` (avec le nouveau total et le
// nombre de donateurs) vers une salle par cagnotte. La page cagnotte s'y
// abonnait, PAS la page de l'association : ses montants ne bougeaient
// qu'au rechargement (2026-08-10). On rejoint donc les salles de toutes
// les cagnottes affichées et on met à jour la carte concernée.
//
// Le socket ne couvre que les dons survenant pendant que la page est
// ouverte ; la valeur d'ARRIVÉE est garantie exacte côté API, qui relit
// les compteurs hors cache (cf. services/liveCounters.ts). Les deux sont
// nécessaires : l'un donne le bon point de départ, l'autre le mouvement.
const { connect: connectCampaignsSocket, joinCampaignRooms, onCampaignDonation } =
  useCampaignSocket();
const { annoncerEnDirect } = useToastEnDirect();
// Dernier don (moins de 7 jours) sur les cagnottes affichées, à l'arrivée.
useAnnonceDerniereActivite(
  () => org.value?.o?.derniere_activite,
  (a, recente) => phraseDon(t, a.montant ? formatAmount(a.montant) : null, recente, a.nom),
);

onMounted(() => {
  const cagnottes = _ssr?.campaigns ?? [];
  if (cagnottes.length === 0) return;

  connectCampaignsSocket();
  joinCampaignRooms(cagnottes.map((c) => c.id));

  onCampaignDonation((data) => {
    const cible = cagnottes.find((c) => String(c.id) === String(data.campaignId));
    if (!cible) return;

    cible.raised = data.newTotalAmount;
    cible.donors_count = data.donorCount;
    if (cible.goal_amount > 0) {
      cible.percent = Math.round((data.newTotalAmount / cible.goal_amount) * 100);
    }
    // Même message en direct que la page de la cagnotte (Karim 2026-10-10) ;
    // le nom seulement si cette cagnotte publie ses donateurs (2026-10-22).
    annoncerEnDirect(phraseDon(t, formatAmount(data.amount), true, cible.show_donors_list ? data.donorName : null));
  });
});

// Scroll detection : sticky nav + active section
// (testimonials carousel-overflow géré dans AssociationsPageTestimonials)
onMounted(() => {
  const handleScroll = () => {
    if (navRef.value) {
      const rect = navRef.value.getBoundingClientRect();
      isNavSticky.value = rect.top <= 0;
      navHeight.value = navRef.value.offsetHeight;
    }

    // Revenu en haut de page : plus de section « en cours », et la ligne des
    // liens (qui défile sur mobile) revient au début.
    if (!isNavSticky.value) {
      if (activeSection.value) {
        activeSection.value = '';
        navLinksRowRef.value?.scrollTo({ left: 0, behavior: 'smooth' });
      }
      return;
    }

    const visibleSections = sections.value.filter((s, idx) => {
      const btn = navButtonRefs.value[idx];
      return btn && btn.offsetParent !== null;
    });
    // eslint-disable-next-line no-restricted-syntax -- liste locale filtrée des sections nav DOM
    if (visibleSections.length === 0) return;

    let best: string | null = null;
    let bestDist = Infinity;
    for (const s of visibleSections) {
      const el = document.getElementById(`section-${s.id}`);
      if (!el) continue;
      const top = el.getBoundingClientRect().top;
      if (top <= window.innerHeight * 0.5) {
        const dist = Math.abs(top);
        if (dist < bestDist) {
          bestDist = dist;
          best = s.id;
        }
      }
    }
    if (best && best !== activeSection.value) {
      activeSection.value = best;
      nextTick(updateIndicator);
    }
  };

  window.addEventListener("scroll", handleScroll, { passive: true });
  onUnmounted(() => window.removeEventListener("scroll", handleScroll));
});

</script>

<template>
  <div class="min-h-screen">
    <!-- 404 -->
    <AssociationsPageNotFound v-if="!_ssr" />

    <!-- Page non encore configurée : asso existe (slug réservé) mais
         n'a pas encore renseigné son RNA → vue dédiée avec logo + nom. -->
    <AssociationsPageIncomplete
      v-else-if="_ssr.incomplete"
      :name="_ssr.name"
      :logo="_ssr.logo"
      :cover-image="_ssr.cover_image"
      :fallback-banner="_ssr.fallback_banner"
    />

    <!-- Surveillance : asso suspended/banned → OrgUnavailable -->
    <OrgUnavailable
      v-else-if="_ssr.risk_status === 'suspended' || _ssr.risk_status === 'banned'"
      :risk-status="_ssr.risk_status"
      :reason="_ssr.risk_reason"
      :organization-name="_ssr.name"
    />

    <!-- Content -->
    <div v-else ref="racine">
      <!-- Bannière FIXE : la page glisse par-dessus, comme sur la page
           billetterie. Karim 2026-09-12. -->
      <AssociationsPageBanniereFixe
        :name="_ssr.name"
        :logo="_ssr.logo"
        :cover-image="_ssr.cover_image"
        :fallback-banner="_ssr.fallback_banner"
        :assets-url="assetsUrl"
      />

      <!-- ⚠️ REMONTE PAR-DESSUS LA BANNIÈRE FIXE. `-mt-[68px]` couvre l'écart
           PLUS les 28 px du rayon : sans ça l'arrondi ne se détache sur rien
           au repos. `z-10` passe au-dessus de la bannière (`z-0`). Mêmes
           valeurs que la page billetterie, pour que les deux se ressemblent
           vraiment. -->
      <div class="relative z-10 -mt-[68px] rounded-t-[28px] bg-white pt-8">
      <AssociationsPageHeader
        :org="_ssr"
        :assets-url="assetsUrl"
        :category-label="categoryLabel"
        :get-logo-url="getLogoUrl"
      >
        <AssociationsPageChiffres
          :total-collecte="_ssr.total_raised"
          :total-donateurs="_ssr.total_donors"
          :display="_ssr.display"
          :format-amount="formatAmount"
        />
      </AssociationsPageHeader>

      <!-- Fil d'Ariane visible : Accueil › Associations › {Ville} › {Asso}.
           Maillage interne vers le hub ville + le listing (SEO), et émet le
           JSON-LD BreadcrumbList (source unique). -->
      <div class="mx-auto max-w-[1120px] px-4 sm:px-8">
        <SeoBreadcrumb :items="breadcrumbItems" :hide-visual="true" />
      </div>

      <!-- Section navbar -->
      <nav v-if="sections.length > 0" ref="navRef" class="section-nav sticky top-0 z-40 mt-12 bg-white/80 backdrop-blur-xl backdrop-saturate-150 transition-[box-shadow] duration-300" :class="isNavSticky ? 'section-nav--sticky' : ''">
        <!-- Ligne des liens (scrollable horizontalement) -->
        <div ref="navLinksRowRef" class="section-nav-links-row" :class="isNavSticky ? 'section-nav-links-row--sticky' : ''">
          <div ref="navListRef" class="section-nav-list relative" :class="isNavSticky ? 'section-nav-list--sticky' : ''">
            <button
              v-for="(section, idx) in sections"
              :key="section.id"
              :ref="el => { if (el) navButtonRefs[idx] = el as HTMLElement }"
              class="section-nav-item"
              :class="{ 'section-nav-item--active': isNavSticky && activeSection === section.id }"
              @click="scrollToSection(section.id)"
            >
              {{ section.label }}
            </button>
            <span
              v-if="isNavSticky && activeSection"
              class="section-nav-indicator"
              :style="indicatorStyle"
            />
          </div>
        </div>
      </nav>
      <!-- Sans barre des sections (fiche sans aucun chapitre) : le nom ne doit
           pas toucher le bloc gris suivant (Karim 28/10/2026). -->
      <div v-else class="h-12 sm:h-16" aria-hidden="true" />
      <!-- Bouton soutenir fixé à droite du viewport — desktop seulement -->
      <Transition name="nav-btn">
        <a
          v-if="isNavSticky && _ssr?.payment_slug"
          :href="`/pay/${_ssr.payment_slug}`"
          target="_blank"
          class="nav-support-btn"
          :style="{ top: `${navHeight / 2}px`, transform: 'translateY(-50%)' }"
        >
          <UIcon name="i-lucide-heart" class="size-4" />
          {{ t("association.page.soutenir") }}
        </a>
      </Transition>

      <!-- Contenus : un chapitre par contenu, en rangées de grandes cartes
           qui défilent (fonds alternés, cf. AssociationsPageRightColumn). -->
      <div class="pt-6">
        <AssociationsPageRightColumn
          :org="_ssr"
          :slug="slug"
          :youtube-embed-url="youtubeEmbedUrl"
          :format-amount="formatAmount"
          :format-date="formatDate"
          :strip-html="stripHtml"
          :get-image-url="getImageUrl"
          @open-modal="openModal"
          @open-post="openPost"
        />
      </div>

      <!-- Informations pratiques + galerie : tuiles blanches sur fond gris. -->
      <AssociationsPageInfoColumn
        :org="_ssr"
        :hub-ville-path="orgVille ? `/associations/ville/${orgVille.cle}` : null"
        :hub-ville-name="orgLieu ?? orgVille?.nom ?? null"
        :assets-url="assetsUrl"
        :has-social-links="hasSocialLinks"
        :format-amount="formatAmount"
        :format-opening-hours="formatOpeningHours"
        :get-image-url="getImageUrl"
      />

    <!-- Témoignages (pleine largeur, défilement continu s'ils débordent) -->
    <AssociationsPageTestimonials
      v-if="_ssr && _ssr.testimonials.length > 0"
      class="pt-20 lg:pt-28"
      :testimonials="_ssr.testimonials"
      :assets-url="assetsUrl"
    />

    <!-- FAQ -->
    <FaqSectionApple
      v-if="_ssr && _ssr.faq.length > 0"
      ancre="section-faq"
      class="pb-0 lg:pb-0"
      :items="_ssr.faq"
      :titre="t('association.page.faqTitre')"
      :texte="t('association.page.faqTexte')"
    />

    <div class="pt-20 lg:pt-28" />

    <!-- CTA Donoor -->
    <AssociationsPageDonoorCTA v-if="_ssr" />
    </div>

    <!-- Modals "Voir plus" -->
    <AssociationsPageVoirPlusModal
      :open="modalOpen"
      :items="modalItems"
      :loading="modalLoading"
      :loading-more="modalLoadingMore"
      :has-more="modalHasMore"
      :get-image-url="getImageUrl"
      :format-amount="formatAmount"
      :format-date="formatDate"
      :strip-html="stripHtml"
      @close="closeModal"
      @load-more="loadMore"
      @open-post="openPost"
    />

    <!-- Modal "article complet" (actualité entière formatée) -->
    <AssociationsPagePostModal
      :post="selectedPost"
      :get-image-url="getImageUrl"
      :format-date="formatDate"
      @close="closePost"
    />

    <!-- « Découvrez d'autres associations » — UNIQUEMENT sur les pages EN COURS
         DE CONFIGURATION (Karim 2026-08-06, même décision que /c /a /b où les
         sections équivalentes sont masquées depuis le 08-06).
         AVANT : ce bloc était HORS de la chaîne v-if/v-else ci-dessus, donc
         rendu sur les QUATRE états — 404, en configuration, suspendue ET page
         fonctionnelle. Une association qui a soigné sa page se retrouvait avec
         10 liens vers d'autres associations en bas de chez elle.
         DEUX RAISONS DE LE RETIRER DES PAGES FONCTIONNELLES :
           1. relation asso — personne ne veut ses confrères sur sa propre page ;
           2. SEO — ces pages souffrent déjà d'un déficit de contenu unique
              (~30 mots propres pour ~895 mots de gabarit, diagnostic 08-05).
              Dix liens partagés par toutes les fiches, c'est du gabarit en plus
              et du contenu unique en moins.
         CE QUE ÇA NE COÛTE PAS : le maillage inter-fiches ne dépend plus de ce
         bloc. Il passe désormais par les hubs ville / département / région et
         par la pagination des rayons (refonte 08-06) — Google atteint les 46
         fiches sans lui.
         Sur une page en configuration il n'y a rien à montrer : les suggestions
         évitent un cul-de-sac. Le cas « suspendue » est volontairement exclu :
         la page reste celle de l'association, on n'y met pas ses concurrentes.
         Le 404 a ses propres suggestions (AssociationsPageNotFound). -->
    <OrganizationRelatedSection v-if="_ssr?.incomplete" :slug="slug" />

      </div>
      <!-- fin du panneau arrondi -->

    <!-- Floating Soutenir button -->
    <SupportDonoor
      v-if="_ssr?.payment_slug"
      :name="_ssr.name"
      :url="`/pay/${_ssr.payment_slug}`"
    />

    <!-- SQUELETTE DE CHARGEMENT SUPPRIMÉ (Karim 2026-08-06, repéré au test
         local sur /associations/seed-org-900003).
         Il était chaîné en `v-else-if="_ssr"` sur le bouton flottant
         « Soutenir » ci-dessus. Conséquence : dès qu'une association n'avait
         PAS de `payment_slug`, un faux écran de chargement (bannière + logo +
         titre en gris pulsant) s'affichait EN PERMANENCE en bas de sa page.
         Mesuré en production : 25 pages association sur 44.
         C'était du code mort. Le squelette dessine le HAUT de page, il datait
         d'une version rendue côté client ; depuis le passage au SSR il n'y a
         plus d'état de chargement — `_ssr` est résolu au rendu serveur, et la
         chaîne v-if du haut couvre déjà les quatre états réels
         (404 / en configuration / suspendue / contenu).
         Le `v-else-if` s'était retrouvé accroché au mauvais frère. -->
  </div>
</template>

<style scoped>
:deep(.prose img[data-size="small"]) {
  max-width: 120px;
}
:deep(.prose img[data-size="medium"]) {
  max-width: 250px;
}
:deep(.prose img[data-size="large"]) {
  max-width: 400px;
}
:deep(.prose img[data-size="full"]) {
  max-width: 100%;
}
:deep(.prose img) {
  border-radius: 0.5rem;
  height: auto;
}

.testimonial-content :deep(p) {
  margin: 0;
}

/* Bouton « Soutenir » qui apparaît à droite quand la barre des sections
   colle en haut — même pilule que BoutonApple (survol un cran PLUS FONCÉ). */
.nav-support-btn {
  position: fixed;
  right: 20px;
  z-index: 41;
  /* Caché sur mobile : le bouton flottant « Soutenir » y suffit. */
  display: none;
  align-items: center;
  gap: 0.375rem;
  padding: 0.55rem 1.15rem;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  background-color: var(--accent, var(--color-main));
  color: var(--accent-text, white);
  border-radius: 980px;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background-color 0.25s ease,
    box-shadow 0.35s ease;
}

@media (min-width: 640px) {
  .nav-support-btn {
    display: inline-flex;
  }
}
@media (hover: hover) {
  .nav-support-btn:hover {
    background: color-mix(in oklab, var(--accent, var(--color-main)), #000 18%);
    box-shadow: 0 10px 24px -8px color-mix(in oklab, var(--accent, var(--color-main)), transparent 45%);
  }
}

.nav-btn-enter-active,
.nav-btn-leave-active {
  transition: opacity 0.25s ease;
}
.nav-btn-enter-from,
.nav-btn-leave-to {
  opacity: 0;
}

/* Barre des sections, façon « barre locale » Apple : verre dépoli, liens
   discrets, la section en cours sur une pilule graphite qui glisse. */
.section-nav--sticky {
  box-shadow: 0 1px 0 rgba(0, 0, 0, 0.08);
}

/* Ligne des liens : scrollable horizontalement */
.section-nav-links-row {
  overflow-x: auto;
  scrollbar-width: none;
  padding: 0 1rem;
}
.section-nav-links-row::-webkit-scrollbar {
  display: none;
}

.section-nav-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.875rem 0;
  width: max-content;
  min-width: 100%;
  white-space: nowrap;
  transition: padding 0.3s ease;
}

.section-nav-list--sticky {
  padding: 0.5rem 0;
}

.section-nav-item {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 15px;
  font-weight: 500;
  color: #424245;
  border-radius: 9999px;
  transition:
    color 0.3s ease,
    background-color 0.3s ease;
  cursor: pointer;
  background: transparent;
  border: none;
  outline: none;
  flex-shrink: 0;
}

@media (hover: hover) {
  .section-nav-item:hover {
    color: #1d1d1f;
    background-color: #f5f5f7;
  }
}
.section-nav-item:focus-visible {
  box-shadow: 0 0 0 3px color-mix(in oklab, var(--color-main), transparent 65%);
}

.section-nav-item--active,
.section-nav-item--active:hover {
  color: #fff;
  font-weight: 600;
  background-color: transparent;
}

.section-nav-indicator {
  position: absolute;
  top: 50%;
  height: 36px;
  transform: translateY(-50%);
  background-color: #1d1d1f;
  border-radius: 9999px;
  transition:
    left 0.35s cubic-bezier(0.28, 0, 0.22, 1),
    width 0.35s cubic-bezier(0.28, 0, 0.22, 1);
  pointer-events: none;
}

</style>
